import { useContainerLayout, useTheme } from '@chronosphereio/chrono-ui';
import { useMemo } from 'react';
import { useVizContainerContext } from '../../context/VizContainerContextProvider';

export interface UseVizContainerLayoutProps {}

// Note: this hook primarily returns numbers because we will eventually need them to do math to
// support backfilling `VizPanel` to use `VizContainer`.
export interface UseVizContainerLayoutResponse {
  /**
   * Layout to apply to the `Container`.
   */
  container: {
    paddingY: number;
    paddingX: number;
  };

  /**
   * Layout to apply to the `Container` body's inner div.
   */
  containerBodyInner: {
    paddingY: number;
    paddingX: number;
    borderBottomLeftRadius: number;
    borderBottomRightRadius: number;
  };

  /**
   * Layout to apply to the `Container`'s header.
   */
  header: {
    marginY: number;
    marginX: number;
  };
}

/**
 * Hook that provides some constants related to the layout of the `VizContainer`.
 */
export const useVizContainerLayout = (): UseVizContainerLayoutResponse => {
  const { theme } = useTheme();
  const containerLayout = useContainerLayout();
  const { variant, disablePadding } = useVizContainerContext();

  const spacingPixels = parseFloat(theme.spacing(1));
  const borderRadius: number =
    typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius : parseFloat(theme.shape.borderRadius);

  // Padding that would be used for the specified variant if `noPadding` was NOT enabled. This
  // helps us set fallback padding on the header when `noPadding` is enabled.
  const defaultContainerPadding: UseVizContainerLayoutResponse['container'] = useMemo(() => {
    if (variant === 'subtle') {
      // Override the default `Container` padding only for the `subtle` header variant as per the designs
      return {
        paddingY: 0,
        paddingX: spacingPixels,
      };
    }

    return {
      paddingY: containerLayout.container.paddingY,
      paddingX: containerLayout.container.paddingX,
    };
  }, [containerLayout.container.paddingX, containerLayout.container.paddingY, spacingPixels, variant]);

  const container: UseVizContainerLayoutResponse['container'] = useMemo(() => {
    if (disablePadding) {
      return {
        paddingY: 0,
        paddingX: 0,
      };
    }

    return defaultContainerPadding;
  }, [defaultContainerPadding, disablePadding]);

  const containerBodyInner: UseVizContainerLayoutResponse['containerBodyInner'] = useMemo(() => {
    if (disablePadding) {
      return {
        paddingY: 0,
        paddingX: 0,

        // When padding is disabled the body inner's bottom border radii need to match the `Container` to
        // avoid sharp edges showing.
        borderBottomLeftRadius: borderRadius,
        borderBottomRightRadius: borderRadius,
      };
    }

    return {
      paddingY: containerLayout.body.paddingY,
      paddingX: containerLayout.body.paddingX,

      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    };
  }, [borderRadius, containerLayout.body.paddingX, containerLayout.body.paddingY, disablePadding]);

  const header: UseVizContainerLayoutResponse['header'] = useMemo(() => {
    if (disablePadding) {
      // When padding is disabled, the header cannot rely on the outer container padding to
      // properly space the header, so we instead need to set it on the header itself. Margins are
      // used instead of padding here because we are generating spacing between the header and
      // the container (not inside the header), and it avoids more complex css issues with padding,
      // height, and box-sizing for the header.
      return {
        marginY: defaultContainerPadding.paddingY,
        marginX: defaultContainerPadding.paddingX,
      };
    }

    return {
      marginY: 0,
      marginX: 0,
    };
  }, [defaultContainerPadding.paddingX, defaultContainerPadding.paddingY, disablePadding]);

  return useMemo(() => {
    return {
      container,
      containerBodyInner,
      header,
    };
  }, [container, containerBodyInner, header]);
};
